import React, { useState, useRef } from 'react';
import { SurveyData, DynamicQuestion } from '../types';
import { extractStudentsFromSurvey, saveSurvey } from '../services/storage';
import { X, UploadCloud, CheckCircle2, AlertCircle, Loader, Trash2, ArrowRight, UserPlus, Image as ImageIcon, Sparkles } from 'lucide-react';
import { useToast } from './Toast';

interface BulkPhotoImporterProps {
  surveys: SurveyData[];
  questions: DynamicQuestion[];
  onClose: () => void;
  onSave: (updatedSurveys: SurveyData[]) => void;
}

interface StudentMapping {
  surveyId: string;
  studentId: string; // surveyId + '-main' or '-rep-X'
  studentName: string;
  serialNo: string;
  category: string;
  currentPhoto: string;
  mappedPhotoBase64: string | null;
  fileName: string | null;
}

export const BulkPhotoImporter: React.FC<BulkPhotoImporterProps> = ({
  surveys,
  questions,
  onClose,
  onSave,
}) => {
  const [students, setStudents] = useState<StudentMapping[]>(() => {
    // Extract and sort students by serial number
    const extracted: StudentMapping[] = [];
    surveys.forEach(survey => {
      const studs = extractStudentsFromSurvey(survey, questions);
      studs.forEach(s => {
        extracted.push({
          surveyId: s.surveyId || survey.id,
          studentId: s.id,
          studentName: s.studentName,
          serialNo: s.serialNo || 'PND',
          category: s.category || '',
          currentPhoto: s.photo || s.studentPhoto || '',
          mappedPhotoBase64: null,
          fileName: null,
        });
      });
    });

    // Sort alphabetically by serial number
    return extracted.sort((a, b) => {
      const sA = String(a.serialNo || '').padStart(6, '0');
      const sB = String(b.serialNo || '').padStart(6, '0');
      return sA.localeCompare(sB);
    });
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [mapTarget, setMapTarget] = useState<'all' | 'missing'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const individualInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { addToast } = useToast();

  // Helper to crop and resize image to lightweight 3:4 aspect ratio JPEG (~10KB-15KB)
  const processAndResizePhoto = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 150;
          canvas.height = 200;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Center crop the image into the 150x200 canvas
          const imgRatio = img.width / img.height;
          const targetRatio = 150 / 200;
          let sx = 0, sy = 0, sw = img.width, sh = img.height;

          if (imgRatio > targetRatio) {
            // Image is wider than 3:4, crop sides
            sw = img.height * targetRatio;
            sx = (img.width - sw) / 2;
          } else {
            // Image is taller than 3:4, crop top/bottom
            sh = img.width / targetRatio;
            sy = (img.height - sh) / 2;
          }

          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 150, 200);
          resolve(canvas.toDataURL('image/jpeg', 0.7)); // Compress to 70% quality JPEG (~10KB)
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    try {
      // Sort files by filename alphabetically to maintain predictable folder structure order
      const fileList = Array.from(files).sort((a, b) => a.name.localeCompare(b.name));
      const processedBase64s: { data: string; name: string }[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (!file.type.startsWith('image/')) continue;
        try {
          const base64 = await processAndResizePhoto(file);
          processedBase64s.push({ data: base64, name: file.name });
        } catch (err) {
          console.error(`Failed to process ${file.name}:`, err);
        }
      }

      if (processedBase64s.length === 0) {
        addToast('No valid image files found.', 'error');
        return;
      }

      // Map to students list based on selected target criteria
      setStudents(prev => {
        let fileIdx = 0;
        return prev.map(student => {
          // If we only want missing and student already has a photo, skip
          if (mapTarget === 'missing' && student.currentPhoto) {
            return student;
          }

          // If we still have processed photos left, map it
          if (fileIdx < processedBase64s.length) {
            const mapped = {
              ...student,
              mappedPhotoBase64: processedBase64s[fileIdx].data,
              fileName: processedBase64s[fileIdx].name,
            };
            fileIdx++;
            return mapped;
          }

          return student;
        });
      });

      addToast(`Successfully processed and mapped ${processedBase64s.length} photos!`, 'success');
    } catch (err) {
      console.error(err);
      addToast('An error occurred while processing bulk photos.', 'error');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleIndividualUpload = async (studentId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      addToast('Please upload a valid image file.', 'error');
      return;
    }

    try {
      const base64 = await processAndResizePhoto(file);
      setStudents(prev => prev.map(s => {
        if (s.studentId === studentId) {
          return {
            ...s,
            mappedPhotoBase64: base64,
            fileName: file.name,
          };
        }
        return s;
      }));
      addToast('Photo mapped successfully!', 'success');
    } catch (err) {
      console.error(err);
      addToast('Failed to process individual photo.', 'error');
    }
  };

  const clearMapping = (studentId: string) => {
    setStudents(prev => prev.map(s => {
      if (s.studentId === studentId) {
        return {
          ...s,
          mappedPhotoBase64: null,
          fileName: null,
        };
      }
      return s;
    }));
  };

  const clearAllMappings = () => {
    setStudents(prev => prev.map(s => ({
      ...s,
      mappedPhotoBase64: null,
      fileName: null,
    })));
    addToast('All mappings cleared.', 'success');
  };

  const saveAllMappedPhotos = async () => {
    const studentsWithNewPhotos = students.filter(s => s.mappedPhotoBase64 !== null);
    if (studentsWithNewPhotos.length === 0) {
      addToast('No new photos mapped to save.', 'error');
      return;
    }

    setIsSaving(true);
    setSavingProgress({ current: 0, total: studentsWithNewPhotos.length });

    try {
      // Group mappings by surveyId to minimize Firestore read/write operations
      const surveyIdToNewPhotos: Record<string, Record<string, string>> = {};
      studentsWithNewPhotos.forEach(s => {
        if (!surveyIdToNewPhotos[s.surveyId]) {
          surveyIdToNewPhotos[s.surveyId] = {};
        }
        surveyIdToNewPhotos[s.surveyId][s.studentId] = s.mappedPhotoBase64!;
      });

      const updatedSurveysList: SurveyData[] = [];
      const surveyIds = Object.keys(surveyIdToNewPhotos);

      for (let i = 0; i < surveyIds.length; i++) {
        const sId = surveyIds[i];
        const newPhotosMap = surveyIdToNewPhotos[sId];

        // Find the original survey object
        const originalSurvey = surveys.find(s => s.id === sId);
        if (!originalSurvey) continue;

        // Perform a deep copy of the survey to update safely
        let updatedSurvey = JSON.parse(JSON.stringify(originalSurvey)) as SurveyData;

        // Locate any image-type question configured for the form
        const imageQ = questions.find(q => q.type === 'image');
        const imageQId = imageQ ? imageQ.id : 'photo';

        // Apply all student photo updates for this survey
        Object.keys(newPhotosMap).forEach(studentId => {
          const base64 = newPhotosMap[studentId];

          if (studentId.endsWith('-main')) {
            updatedSurvey.sectionA = updatedSurvey.sectionA || {};
            updatedSurvey.sectionA[imageQId] = base64;
            updatedSurvey.sectionA.photo = base64;
            updatedSurvey.sectionA.studentPhoto = base64;
          } else if (studentId.includes('-rep-')) {
            const parts = studentId.split('-rep-');
            const idx = parseInt(parts[parts.length - 1], 10);
            
            // Iterate all array fields to find the matched member array
            const arrayKeys = Object.keys(updatedSurvey).filter(key => Array.isArray((updatedSurvey as any)[key]));
            arrayKeys.forEach(key => {
              const arr = updatedSurvey[key] || [];
              if (arr[idx] !== undefined && arr[idx] !== null && typeof arr[idx] === 'object') {
                arr[idx][imageQId] = base64;
                arr[idx].photo = base64;
                arr[idx].studentPhoto = base64;
              }
            });
          }
        });

        // Save the updated survey to Firestore
        await saveSurvey(updatedSurvey);
        updatedSurveysList.push(updatedSurvey);

        // Update progress counter
        const countOfProcessedStudents = Object.keys(newPhotosMap).length;
        setSavingProgress(prev => ({
          ...prev,
          current: Math.min(prev.current + countOfProcessedStudents, prev.total),
        }));
      }

      // Notify parent of updated surveys to trigger react state refresh
      onSave(updatedSurveysList);
      addToast(`Successfully saved ${studentsWithNewPhotos.length} student photos to the database!`, 'success');
      onClose();
    } catch (err: any) {
      console.error(err);
      addToast(err.message || 'Failed to save mapped photos.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const mappedCount = students.filter(s => s.mappedPhotoBase64 !== null).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full flex flex-col h-[85vh] border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="bg-emerald-50 text-emerald-600 p-2 rounded-xl">
              <Sparkles size={22} className="animate-pulse" />
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-800">Automatic Bulk Photo Importer</h3>
              <p className="text-xs text-gray-400">Map uploaded photo files directly to students sorted by Serial Number order.</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body Container */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-6 min-h-0">
          
          {/* Left panel: Upload controls and mapping parameters */}
          <div className="lg:w-1/3 flex flex-col gap-5 flex-shrink-0">
            <div className="bg-gray-50 border border-gray-200/60 p-5 rounded-2xl space-y-4">
              <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider">1. Select Target Criteria</h4>
              
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMapTarget('all')}
                  className={`py-2.5 px-3 rounded-xl border text-xs font-bold transition-all ${
                    mapTarget === 'all' 
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All Students
                </button>
                <button
                  type="button"
                  onClick={() => setMapTarget('missing')}
                  className={`py-2.5 px-3 rounded-xl border text-xs font-bold transition-all ${
                    mapTarget === 'missing' 
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  No Photo Only
                </button>
              </div>
              <p className="text-[11px] text-gray-400 leading-normal">
                {mapTarget === 'all' 
                  ? 'Photos will be mapped to all registered students starting from Serial 0001.' 
                  : 'Photos will skip students who already have a profile image in the database.'}
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200/60 p-5 rounded-2xl flex-1 flex flex-col justify-center text-center">
              <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider text-left mb-4">2. Upload Folder Photos</h4>
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 border-2 border-dashed border-gray-300 hover:border-emerald-500 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all hover:bg-white bg-gray-50/50 group"
              >
                <UploadCloud className="text-gray-400 group-hover:text-emerald-500 w-12 h-12 mb-3 transition-colors" />
                <span className="text-xs font-bold text-gray-700 block">Select Multiple Images</span>
                <span className="text-[10px] text-gray-400 mt-1">Accepts PNG, JPG (sorted by filename)</span>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  multiple 
                  accept="image/*"
                  onChange={handleBulkUpload}
                  className="hidden" 
                />
              </div>

              {mappedCount > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200/60 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500">{mappedCount} mapped</span>
                  <button
                    type="button"
                    onClick={clearAllMappings}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-bold"
                  >
                    <Trash2 size={13} /> Clear Mappings
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: Active matched preview table */}
          <div className="flex-1 flex flex-col min-w-0 border border-gray-200/80 rounded-2xl overflow-hidden bg-gray-50">
            <div className="bg-white px-5 py-3 border-b border-gray-100 flex justify-between items-center">
              <span className="text-xs font-black text-gray-400 uppercase tracking-wider">Sorted Students List ({students.length})</span>
              <span className="text-[10px] bg-gray-100 text-gray-500 font-bold px-2 py-0.5 rounded">Ordered by Serial No</span>
            </div>

            {/* Scrollable List of Students */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100 bg-white">
              {students.map((student, idx) => (
                <div key={student.studentId} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono font-bold bg-gray-100 text-gray-500 px-2 py-1 rounded">
                      {student.serialNo}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{student.studentName}</p>
                      <p className="text-[10px] text-emerald-600 font-black uppercase">{student.category}</p>
                    </div>
                  </div>

                  {/* Photo Comparison & Mapping UI */}
                  <div className="flex items-center gap-4">
                    {/* Current Photo */}
                    <div className="flex flex-col items-center">
                      <p className="text-[9px] text-gray-400 uppercase font-bold mb-1">Current</p>
                      <div className="w-12 h-16 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                        {student.currentPhoto ? (
                          <img 
                            src={student.currentPhoto} 
                            alt="Current" 
                            className="w-full h-full object-cover" 
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="text-[9px] text-gray-400 uppercase italic">None</span>
                        )}
                      </div>
                    </div>

                    <ArrowRight className="text-gray-300" size={16} />

                    {/* New Photo */}
                    <div className="flex flex-col items-center">
                      <p className="text-[9px] text-gray-400 uppercase font-bold mb-1">Incoming</p>
                      <div className="w-12 h-16 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden flex items-center justify-center relative bg-gray-50 group">
                        {student.mappedPhotoBase64 ? (
                          <>
                            <img 
                              src={student.mappedPhotoBase64} 
                              alt="Mapped" 
                              className="w-full h-full object-cover" 
                            />
                            <button
                              type="button"
                              onClick={() => clearMapping(student.studentId)}
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white rounded-lg"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => individualInputRefs.current[student.studentId]?.click()}
                            className="text-gray-400 hover:text-emerald-500"
                            title="Assign Single Photo"
                          >
                            <UserPlus size={16} />
                          </button>
                        )}
                        <input
                          type="file"
                          ref={el => individualInputRefs.current[student.studentId] = el}
                          accept="image/*"
                          onChange={(e) => handleIndividualUpload(student.studentId, e)}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0 flex justify-between items-center rounded-b-3xl">
          <div className="text-left">
            {mappedCount > 0 && (
              <p className="text-xs font-bold text-gray-600">
                Ready to save <span className="text-emerald-600 font-extrabold">{mappedCount}</span> new photo assignments.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 font-bold text-sm transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={saveAllMappedPhotos}
              disabled={isSaving || isProcessing || mappedCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl font-extrabold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  <span>Saving ({savingProgress.current}/{savingProgress.total})...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  <span>Save Mapped Photos</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
